import { useCallback, useRef, useState, type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ListIcon from '@mui/icons-material/FormatListBulleted';
import {
    FormSectionNavContext,
    hasNav,
    useFormSectionNav,
    type FormSection
} from './formSectionNavContext';

/**
 * In-form section navigation for the fullscreen record dialogs: a sticky left
 * rail from `md` up, and a "jump to section" app-bar Menu below it.
 *
 * A dialog wraps its form in {@link FormSectionNavProvider} with its ordered
 * section list, drops {@link SectionNavMenuButton} into the app bar, and pairs
 * every {@link FormSection} with a matching `anchorId`. {@link FormSectionsBody}
 * then renders the rail, tracks the scroll position, and highlights the section
 * you are in.
 *
 * The rail is a layout sibling of the scroll area (not a floating overlay), so
 * it needs no z-index games; the mobile affordance is a Menu, not a Drawer,
 * because a Drawer inside a fullscreen Dialog opens behind it — see
 * swipeable-drawer-overlay-gotcha.
 */
/** Gap (px) between the rail and the section-box column. */
const SECTION_NAV_GAP = 16;
/**
 * Rail width: whatever the left gutter leaves, between 160 and 220px. It floats
 * in that gutter, so it never shifts the column.
 *
 * The gutter either side of the 800px column is `(100vw - 48 page padding -
 * 800) / 2`, less 10px for a scrollbar, and the rail needs `width + GAP` of it.
 * At `lg` (1200px) that is the 160 floor; German section headings are long
 * unbreakable compounds ("Grabungsmannschaft"), and at 160 the longest of them
 * break — so every pixel of gutter above `lg` goes to the labels, up to 220 at
 * about 1340px.
 */
const SECTION_NAV_RAIL_WIDTH = `clamp(160px, calc((100vw - 848px) / 2 - 10px - ${SECTION_NAV_GAP}px), 220px)`;

export function FormSectionNavProvider({
    sections,
    children
}: {
    sections: FormSection[];
    children: ReactNode;
}) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const setContainer = useCallback((node: HTMLDivElement | null) => {
        containerRef.current = node;
    }, []);
    const [activeId, setActiveId] = useState<string | null>(
        sections[0]?.id ?? null
    );

    function scrollTo(id: string) {
        const container = containerRef.current;
        const target = container?.querySelector<HTMLElement>(
            `[data-section-anchor="${CSS.escape(id)}"]`
        );
        if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            // Reflect the choice at once; the scroll spy would only catch up
            // once the smooth scroll settles.
            setActiveId(id);
        }
    }

    return (
        <FormSectionNavContext.Provider
            value={{ sections, activeId, setActiveId, setContainer, scrollTo }}
        >
            {children}
        </FormSectionNavContext.Provider>
    );
}

/**
 * The `lg`+ rail. It floats in the left gutter of the centered box column — an
 * absolutely-positioned holder that spans the column's height (so the column
 * stays page-centered, unshifted) with a sticky inner that pins to the viewport
 * as the form scrolls. Renders nothing on smaller widths or without a nav; below
 * `lg` there is no room beside a centered column, so the app-bar Menu takes over.
 */
export function SectionNavRail() {
    const nav = useFormSectionNav();
    if (!hasNav(nav)) {
        return null;
    }
    return (
        <Box
            aria-hidden={false}
            sx={{
                display: { xs: 'none', lg: 'block' },
                position: 'absolute',
                top: 0,
                bottom: 0,
                // Sit the holder just left of the column, with a small gap.
                right: `calc(100% + ${SECTION_NAV_GAP}px)`,
                width: SECTION_NAV_RAIL_WIDTH
            }}
        >
            <Box
                component="nav"
                sx={{
                    /**
                     * Pin to the viewport top while the holder (== column height)
                     * scrolls past. Transparent — the only marks are the
                     * right-aligned labels and the active item's rule.
                     */
                    position: 'sticky',
                    top: 0,
                    py: 1
                }}
            >
                <Typography
                    variant="overline"
                    sx={{
                        display: 'block',
                        px: 2,
                        textAlign: 'right',
                        color: 'text.secondary'
                    }}
                >
                    <FormattedMessage id="form.sectionNav.title" />
                </Typography>
                <List dense disablePadding>
                    {nav.sections.map((entry) => {
                        const active = entry.id === nav.activeId;
                        return (
                            <ListItemButton
                                key={entry.id}
                                selected={active}
                                onClick={() => nav.scrollTo(entry.id)}
                                sx={{
                                    // Tighter than the List default so the long
                                    // headings keep every pixel of the rail.
                                    px: 1.5,
                                    /**
                                     * Active item marked by a right border in the
                                     * primary colour (next to the box), like the
                                     * main-nav submenu's left border. No background.
                                     */
                                    borderRight: 2,
                                    borderColor: 'transparent',
                                    '&.Mui-selected': {
                                        bgcolor: 'transparent',
                                        borderColor: 'primary.main',
                                        '&:hover': { bgcolor: 'action.hover' }
                                    }
                                }}
                            >
                                <ListItemText
                                    primary={
                                        <FormattedMessage id={entry.titleId} />
                                    }
                                    slotProps={{
                                        primary: {
                                            color: active
                                                ? 'primary.main'
                                                : 'text.secondary',
                                            sx: {
                                                textAlign: 'right',
                                                fontWeight: active ? 600 : 400,
                                                /**
                                                 * A heading may take two lines
                                                 * ("Letzte vergebene Nummern");
                                                 * tighten the leading so it
                                                 * still reads as one item.
                                                 */
                                                lineHeight: 1.3,
                                                /**
                                                 * German compounds are single
                                                 * unbreakable words — break one
                                                 * rather than let it overflow
                                                 * into the clipped gutter.
                                                 */
                                                overflowWrap: 'break-word'
                                            }
                                        }
                                    }}
                                />
                            </ListItemButton>
                        );
                    })}
                </List>
            </Box>
        </Box>
    );
}

/**
 * The "jump to section" app-bar button + Menu. Hidden from `lg` up (where the
 * gutter rail takes over) and absent without a nav; it covers everything below
 * `lg`, where a centered column leaves no room for the rail. Drop it into
 * {@link FormDialogAppBar}'s `startSlot`.
 */
export function SectionNavMenuButton() {
    const intl = useIntl();
    const nav = useFormSectionNav();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    if (!hasNav(nav)) {
        return null;
    }
    const label = intl.formatMessage({ id: 'form.sectionNav.title' });
    return (
        <Box sx={{ display: { xs: 'inline-flex', lg: 'none' } }}>
            <Tooltip title={label}>
                <IconButton
                    edge="start"
                    aria-label={label}
                    onClick={(event) => setAnchor(event.currentTarget)}
                >
                    <ListIcon />
                </IconButton>
            </Tooltip>
            <Menu
                open={!!anchor}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
            >
                {nav.sections.map((entry) => (
                    <MenuItem
                        key={entry.id}
                        selected={entry.id === nav.activeId}
                        onClick={() => {
                            nav.scrollTo(entry.id);
                            setAnchor(null);
                        }}
                    >
                        <FormattedMessage id={entry.titleId} />
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
}
